/**
 * The opener's ON/OFF state, as ONE fact.
 *
 * The header used to publish three unrelated facts side by side — a
 * "Control plane: UP" chip (the `opener-orchestrator` pod), a "No groups
 * running" chip (per-group ENGINE Deployments) and four buttons split across
 * both — and none of them answered the only question an operator actually
 * asks: can I start turning groups on right now?
 *
 * They also left a hole. Rule CRUD is served by `position-opener-node`, a pod
 * neither chip covered: on 2026-08-14 it sat Pending for ~5 minutes after a
 * scale-up while the chip read "Control plane: UP", and every leg toggle came
 * back as a bodyless 503 the UI could only report as "Failed to create the
 * opener rule".
 *
 * So: OPENER ON ⇔ the orchestrator is booted AND the rule-CRUD pod is up.
 * That pod takes BOTH of its presence fields — see `nodeReady` / `nodeServing`
 * below. Group engines are reported alongside as a COUNT, never as the on/off
 * fact.
 */

export type OpenerPowerPhase =
  | 'on'
  | 'booting'
  | 'starting'
  | 'stopping'
  | 'off'
  | 'locked'
  | 'unknown'
  | 'reading';

/** What the single primary button does in this phase. */
export type OpenerPowerAction = 'start' | 'stop' | 'clear-lockout' | 'none';

export interface OpenerPowerInput {
  /**
   * False until the presence query has completed ONE successful read. While it
   * is false nothing about the pods is known — not even "unknown" — so the
   * header must not offer an action off a value it has never received.
   */
  firstReadDone: boolean;
  /** `domains.opener.booted` — the orchestrator pod. `null` = unreadable. */
  booted: boolean | null;
  /**
   * The orchestrator is answering ITS OWN state query. Only consulted when
   * `booted` is unreadable, and it is better evidence than the default the old
   * header used: `/opener/state` and the stop route ride the SAME axios
   * instance, so a fresh 200 on the former proves the latter is reachable.
   */
  controlPlaneAnswering: boolean;
  /**
   * `domains.opener.nodeReady` — the rule-CRUD Deployment's `spec.replicas`.
   * Needed alongside `nodeServing` because that pod runs `strategy: Recreate`
   * with a 30s grace: the instant it is scaled to 0 this goes false while the
   * dying pod still reports a ready replica.
   */
  nodeReady: boolean | null;
  /** `domains.opener.nodeServing` — that pod has a READY replica. */
  nodeServing: boolean | null;
  /** Per-group engine Deployments with ready replicas; `undefined` = unknown. */
  groupEnginesRunning?: number;
  /** A 24h panic lockout is armed (blocks start + rule activation). */
  lockoutArmed: boolean;
  /** Preformatted expiry ("14:32"), shown in the pill so it is not hover-only. */
  lockoutExpiresLabel?: string;
  startJobRunning: boolean;
  stopJobRunning: boolean;
}

export interface OpenerPower {
  phase: OpenerPowerPhase;
  label: string;
  color: 'success' | 'info' | 'warning' | 'error' | 'default';
  action: OpenerPowerAction;
  /** Label for the primary button. */
  actionLabel: string;
  /** True while a transition is in flight — the button is inert. */
  busy: boolean;
}

const groupSuffix = (count?: number): string => {
  if (typeof count !== 'number') return '';
  if (count === 0) return ' · sin grupos';
  return ` · ${count} grupo${count === 1 ? '' : 's'}`;
};

/**
 * Precedence matters, and every step of it is load-bearing:
 *
 * 1. Nothing read yet ⇒ say so and offer NOTHING. The presence hook reports
 *    every field as `null` while its first request is still in flight, which is
 *    indistinguishable from "the backend could not read it" — and on every page
 *    mount that window would otherwise paint a healthy opener as `OPENER ?`
 *    with an enabled Start.
 * 2. An in-flight start/stop wins — it is the only phase the operator caused on
 *    purpose, and both k8s reads lag it.
 * 3. `booted === false` is the only trustworthy OFF, and it comes FIRST among
 *    the steady states because `lockoutArmed` is read from a query the
 *    orchestrator itself serves: once that pod is drained the last snapshot
 *    freezes with whatever it last said, and a stale `armed: true` there would
 *    replace the one button that boots the pod back with "clear the lockout".
 * 4. A lockout outranks BOTH the ON claim and an unreadable `booted`: the plane
 *    may well be up and no group can be started, so hiding it behind `OPENER ?`
 *    sends the operator into a start the control plane will refuse. Only the
 *    positive `booted === false` above suppresses it.
 * 5. `booted === null` (unreadable, or a pre-`domains` backend) is never OFF,
 *    and it is not automatically a START either. ROOT serves that field, a
 *    DIFFERENT pod from the orchestrator these buttons act on: when root is
 *    down but the orchestrator still answers its own state query, the opener is
 *    alive and the control the operator needs is APAGAR. Offering START there
 *    inverts the control over a live opener — the old header, for all its
 *    faults, got this right by defaulting to running. Only when neither pod
 *    answers does START become the right offer, and it is safe because it boots
 *    whatever is missing.
 * 6. Either CRUD field being positively false means the rule API is not
 *    answering — `nodeServing` for the Pending window, `nodeReady` for the
 *    drain window. `null` on either is UNKNOWN (or a backend predating the
 *    field) and must NOT latch the pill — unknown reads as ON, the same
 *    fail-open rule the rest of the presence block follows.
 */
export function deriveOpenerPower(input: OpenerPowerInput): OpenerPower {
  const {
    firstReadDone,
    booted,
    controlPlaneAnswering,
    nodeReady,
    nodeServing,
    groupEnginesRunning,
    lockoutArmed,
    lockoutExpiresLabel,
    startJobRunning,
    stopJobRunning
  } = input;

  if (!firstReadDone) {
    return {
      phase: 'reading',
      label: 'LEYENDO ESTADO…',
      color: 'default',
      action: 'none',
      actionLabel: 'Leyendo estado…',
      busy: true
    };
  }

  if (stopJobRunning) {
    return {
      phase: 'stopping',
      label: 'APAGANDO…',
      color: 'warning',
      action: 'none',
      actionLabel: 'Apagando…',
      busy: true
    };
  }

  if (startJobRunning) {
    return {
      phase: 'starting',
      label: 'PRENDIENDO…',
      color: 'info',
      action: 'none',
      actionLabel: 'Prendiendo…',
      busy: true
    };
  }

  if (booted === false) {
    return {
      phase: 'off',
      // No group count here, unlike `locked`: it comes from a query the
      // orchestrator serves, so with that pod drained the number is a frozen
      // snapshot. The engines really can outlive the control plane — the
      // tooltip says so in prose rather than printing a count that stopped
      // being evidence.
      label: 'OPENER OFF',
      color: 'default',
      action: 'start',
      actionLabel: 'Prender opener',
      busy: false
    };
  }

  if (lockoutArmed) {
    return {
      phase: 'locked',
      label:
        'OPENER BLOQUEADO · lockout 24h' +
        (lockoutExpiresLabel ? ` hasta ${lockoutExpiresLabel}` : '') +
        // The engines already up keep operating through a lockout — it only
        // blocks STARTING more. Dropping the count here read as "nothing is
        // running", the opposite of the truth.
        groupSuffix(groupEnginesRunning),
      color: 'error',
      action: 'clear-lockout',
      actionLabel: 'Limpiar lockout',
      busy: false
    };
  }

  if (booted === null) {
    return controlPlaneAnswering
      ? {
          phase: 'unknown',
          // Deliberately not "ON": the orchestrator answering proves the plane
          // is alive, nothing about the rule-CRUD pod. Says what is known, and
          // offers the action that fits it.
          label: 'OPENER ? · control plane responde',
          color: 'warning',
          action: 'stop',
          actionLabel: 'Apagar opener',
          busy: false
        }
      : {
          phase: 'unknown',
          label: 'OPENER ?',
          color: 'default',
          action: 'start',
          actionLabel: 'Prender opener',
          busy: false
        };
  }

  if (nodeReady === false || nodeServing === false) {
    return {
      phase: 'booting',
      label:
        (nodeReady === false
          ? 'CRUD DE REGLAS CAÍDO'
          : 'ARRANCANDO… (CRUD aún no)') + groupSuffix(groupEnginesRunning),
      // A pod on its way up is informational; one that fell over is not.
      color: nodeReady === false ? 'warning' : 'info',
      action: 'stop',
      actionLabel: 'Apagar opener',
      busy: false
    };
  }

  return {
    phase: 'on',
    label: `OPENER ON${groupSuffix(groupEnginesRunning)}`,
    color: 'success',
    action: 'stop',
    actionLabel: 'Apagar opener',
    busy: false
  };
}

/** Operator-facing explanation of a phase, for the pill's tooltip. */
export function openerPowerTooltip(phase: OpenerPowerPhase): string {
  switch (phase) {
    case 'on':
      return (
        'El opener está PRENDIDO: el control plane (opener-orchestrator) y el ' +
        'CRUD de reglas (position-opener-node) están arriba, así que podés ' +
        'prender grupos y editar reglas. El contador son los engines de grupo ' +
        'que están corriendo.'
      );
    case 'booting':
      return (
        'El control plane está arriba, pero el pod del CRUD de reglas ' +
        '(position-opener-node) no está sirviendo: o todavía no tiene una ' +
        'réplica READY — suele tardar unos minutos si hay que sumar un nodo — ' +
        'o quedó escalado a 0 y root lo re-asegura en ~30s. Hasta que vuelva, ' +
        'crear o prender reglas falla con un error sin detalle.'
      );
    case 'starting':
      return 'Prendiendo el opener — booteando el control plane y el CRUD.';
    case 'stopping':
      return 'Apagando el opener — cancelando órdenes, pausando reglas y bajando el control plane.';
    case 'off':
      return (
        'El opener está APAGADO: el control plane está abajo, no se pueden ' +
        'prender grupos ni editar reglas. OJO: los engines por grupo son pods ' +
        'aparte — los que ya estaban arriba pueden seguir operando.'
      );
    case 'locked':
      return (
        'Hay un lockout de 24h armado: ningún grupo puede arrancar y ninguna ' +
        'regla puede activarse hasta limpiarlo. Los engines que ya están ' +
        'arriba siguen operando.'
      );
    case 'reading':
      return 'Leyendo el estado del opener…';
    case 'unknown':
    default:
      return (
        'No se pudo leer el estado del opener: root (prediction-manager) no ' +
        'respondió o su lectura de k8s falló. Nunca se asume apagado. Si el ' +
        'control plane igual está contestando sus propias consultas, se ofrece ' +
        'APAGAR — está vivo aunque root no lo vea. Si tampoco contesta, se ' +
        'ofrece Prender, que es seguro: bootea lo que falte.'
      );
  }
}
